import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import dayjs from "dayjs";
import { confirmAlert } from "react-confirm-alert";
import { dateFormat } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";
import TextFieldDate from "../../UI/TextFieldDate";
import { diaDaSemana, porData } from "./diaCalendarioUtil";

const ordenar = (lista) => [...lista].sort(porData((d) => d.data));

/** Cadastro dos dias (Letivos Extras ou Não Letivos) do ano letivo. */
const DiaCalendarioList = ({ config }) => {
  const { api } = config;
  const [dias, setDias] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    api
      .findByAnoLetivo()
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          setDias(ordenar(data));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const handleSave = (event) => {
    event.preventDefault();
    if (!editando.data || !dayjs(editando.data).isValid()) {
      errorToast("Informe uma data válida.");
      return;
    }
    const eNovo = !editando.codigo;
    setIsSaving(true);
    api
      .save(editando)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar.");
          return;
        }
        setDias((prevState) =>
          ordenar(eNovo ? [...prevState, data] : prevState.map((d) => (d.codigo === data.codigo ? data : d))),
        );
        setEditando(null);
        success("Salvo com sucesso.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = (dia) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover o ${config.singular.toLowerCase()} de ${dateFormat(dia.data)}?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            api.deleteById(dia.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              setDias((prevState) => prevState.filter((d) => d.codigo !== dia.codigo));
              success("Removido com sucesso.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const renderTabela = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description={`Carregando ${config.plural.toLowerCase()}...`} />
        </Box>
      );
    }
    if (dias.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <EventBusyOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhum {config.singular.toLowerCase()} cadastrado neste ano letivo.
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" aria-label={config.plural}>
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell sx={{ width: 180 }}>Data</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right" sx={{ width: 100 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {dias.map((dia) => (
              <TableRow key={dia.codigo} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {dateFormat(dia.data)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                    {diaDaSemana(dia.data)}
                  </Typography>
                </TableCell>
                <TableCell>{dia.descricao || "--"}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => setEditando(dia)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => handleDelete(dia)}>
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        mb={2}
      >
        <Typography variant="body2" color="text.secondary">
          {config.descricao}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditando({ eNovo: true })}>
          Novo {config.singular}
        </Button>
      </Stack>

      {renderTabela()}

      <Dialog open={Boolean(editando)} onClose={() => setEditando(null)} fullWidth maxWidth="xs">
        <form onSubmit={handleSave}>
          <DialogTitle>{editando?.codigo ? `Editar ${config.singular}` : `Novo ${config.singular}`}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} pt={1}>
              <TextFieldDate
                label="Data"
                value={editando?.data || null}
                onChange={(value) =>
                  setEditando((prevState) => ({ ...prevState, data: value ? dayjs(value) : null }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
              />
              <TextField
                size="small"
                fullWidth
                label="Descrição"
                value={editando?.descricao ?? ""}
                onChange={(event) => setEditando((prevState) => ({ ...prevState, descricao: event.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditando(null)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={isSaving}>
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DiaCalendarioList;
